/**
 * Canvas-based particle animation for Telegram spoiler text.
 *
 * Renders small floating dots over spoiler spans, matching the
 * native Telegram desktop client effect.
 */

// -- Types -------------------------------------------------------------------

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  alpha: number;
}

export interface SpoilerEngineOptions {
  /** 'editor' = pointer-events:none (hover reveal), 'preview' = click reveal */
  mode: 'editor' | 'preview';
}

// -- Manager (singleton RAF loop) --------------------------------------------

class SpoilerAnimationManager {
  private engines = new Set<SpoilerParticleEngine>();
  private rafId: number | null = null;
  private lastTime = 0;

  register(engine: SpoilerParticleEngine): void {
    this.engines.add(engine);
    if (this.engines.size === 1) this.start();
  }

  unregister(engine: SpoilerParticleEngine): void {
    this.engines.delete(engine);
    if (this.engines.size === 0) this.stop();
  }

  private start(): void {
    this.lastTime = performance.now();
    const loop = (now: number) => {
      const dt = Math.min(now - this.lastTime, 32); // cap at ~30 fps minimum
      this.lastTime = now;
      for (const engine of this.engines) {
        engine.tick(dt);
      }
      this.rafId = requestAnimationFrame(loop);
    };
    this.rafId = requestAnimationFrame(loop);
  }

  private stop(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }
}

const manager = new SpoilerAnimationManager();

// -- Engine (per-spoiler instance) -------------------------------------------

const PARTICLE_AREA = 7; // ~1 particle per 7 sq px
const MAX_PARTICLES = 2000;
const BASE_SPEED = 0.015; // px per ms
const MIN_RADIUS = 0.5;
const MAX_RADIUS = 1.5;

export class SpoilerParticleEngine {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private particles: Particle[] = [];
  private ro: ResizeObserver | null = null;
  private ownsCanvas: boolean;
  private width = 0;
  private height = 0;
  private dpr = 1;
  private overlay = 1; // 1 = fully hidden, 0 = revealed
  private targetOverlay = 1;
  private revealed = false;
  private fadeDuration = 300;
  private destroyed = false;

  /**
   * When true the engine is inert (no canvas context available).
   * This happens in test environments (jsdom) that don't support canvas.
   */
  readonly noop: boolean;

  /**
   * Create a particle engine.
   *
   * @param target - Either an HTMLElement (preview mode: engine creates and
   *   appends its own canvas) or an HTMLCanvasElement (editor overlay mode:
   *   engine draws on the provided canvas, caller manages positioning).
   * @param options - Engine options.
   */
  constructor(target: HTMLElement, options: SpoilerEngineOptions) {
    let canvas: HTMLCanvasElement;

    if (target instanceof HTMLCanvasElement) {
      // External canvas mode — caller manages position and lifecycle
      canvas = target;
      this.ownsCanvas = false;
    } else {
      // Host mode — create canvas and append to host
      canvas = document.createElement('canvas');
      this.ownsCanvas = true;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      this.noop = true;
      this.canvas = canvas;
      this.ctx = null as unknown as CanvasRenderingContext2D;
      this.ownsCanvas = false;
      return;
    }

    this.noop = false;
    this.canvas = canvas;
    this.ctx = ctx;

    if (this.ownsCanvas) {
      // Host mode setup
      canvas.style.cssText =
        'position:absolute;inset:0;border-radius:inherit;' +
        (options.mode === 'editor' ? 'pointer-events:none;' : 'pointer-events:auto;');

      const pos = getComputedStyle(target).position;
      if (pos === 'static' || pos === '') {
        target.style.position = 'relative';
      }
      target.appendChild(canvas);

      // Only use ResizeObserver for block-level elements — the CSS spec
      // says ResizeObserver does not support inline elements and
      // implementations may fire callbacks with zero dimensions, which
      // permanently kills the animation (tick returns early).
      const display = getComputedStyle(target).display;
      if (display !== 'inline') {
        this.ro = new ResizeObserver(() => {
          const rect = target.getBoundingClientRect();
          if (rect.width > 0 && rect.height > 0) {
            this.resizeTo(rect.width, rect.height);
          }
        });
        this.ro.observe(target);
      }

      const rect = target.getBoundingClientRect();
      this.resizeTo(rect.width, rect.height);
    }

    manager.register(this);
  }

  /** Resize the canvas to the given CSS dimensions. */
  resizeTo(w: number, h: number): void {
    if (this.noop) return;
    this.dpr = window.devicePixelRatio || 1;
    this.width = w;
    this.height = h;

    this.canvas.width = Math.round(w * this.dpr);
    this.canvas.height = Math.round(h * this.dpr);
    this.canvas.style.width = String(w) + 'px';
    this.canvas.style.height = String(h) + 'px';
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

    this.initParticles();
  }

  /** Animate particles to transparent, revealing text underneath */
  reveal(duration = 300): void {
    this.revealed = true;
    this.targetOverlay = 0;
    this.fadeDuration = duration;
  }

  /** Re-hide text with particles */
  hide(duration = 300): void {
    this.revealed = false;
    this.targetOverlay = 1;
    this.fadeDuration = duration;
  }

  get isRevealed(): boolean {
    return this.revealed;
  }

  /** Called by the animation manager every frame */
  tick(dt: number): void {
    if (this.noop || this.destroyed || this.width === 0 || this.height === 0) return;

    // Animate overlay alpha towards target
    if (this.overlay !== this.targetOverlay) {
      const step = dt / Math.max(this.fadeDuration, 1);
      if (this.targetOverlay < this.overlay) {
        this.overlay = Math.max(this.targetOverlay, this.overlay - step);
      } else {
        this.overlay = Math.min(this.targetOverlay, this.overlay + step);
      }
    }

    // Skip draw when fully revealed
    if (this.overlay <= 0.001) {
      this.ctx.clearRect(0, 0, this.width * this.dpr, this.height * this.dpr);
      return;
    }

    this.updateParticles(dt);
    this.draw();
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    if (this.noop) return;
    manager.unregister(this);
    if (this.ro) this.ro.disconnect();
    if (this.ownsCanvas) this.canvas.remove();
    this.particles = [];
  }

  // -- Internal --------------------------------------------------------------

  private initParticles(): void {
    const area = this.width * this.height;
    const count = Math.min(Math.floor(area / PARTICLE_AREA), MAX_PARTICLES);
    this.particles = [];
    for (let i = 0; i < count; i++) {
      this.particles.push(this.createParticle());
    }
  }

  private createParticle(): Particle {
    const angle = Math.random() * Math.PI * 2;
    const speed = BASE_SPEED * (0.5 + Math.random());
    return {
      x: Math.random() * this.width,
      y: Math.random() * this.height,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      radius: MIN_RADIUS + Math.random() * (MAX_RADIUS - MIN_RADIUS),
      alpha: 0.4 + Math.random() * 0.6,
    };
  }

  private updateParticles(dt: number): void {
    const w = this.width;
    const h = this.height;
    for (const p of this.particles) {
      p.x += p.vx * dt;
      p.y += p.vy * dt;

      // Bounce off edges
      if (p.x < 0) {
        p.x = -p.x;
        p.vx = Math.abs(p.vx);
      } else if (p.x > w) {
        p.x = 2 * w - p.x;
        p.vx = -Math.abs(p.vx);
      }
      if (p.y < 0) {
        p.y = -p.y;
        p.vy = Math.abs(p.vy);
      } else if (p.y > h) {
        p.y = 2 * h - p.y;
        p.vy = -Math.abs(p.vy);
      }
    }
  }

  private draw(): void {
    const ctx = this.ctx;
    const w = this.width;
    const h = this.height;

    ctx.clearRect(0, 0, w, h);

    const isDark = document.documentElement.classList.contains('dark');

    // Background fill (masks text)
    const bgAlpha = String(this.overlay * 0.92);
    if (isDark) {
      ctx.fillStyle = 'rgba(30, 35, 45, ' + bgAlpha + ')';
    } else {
      ctx.fillStyle = 'rgba(180, 185, 195, ' + bgAlpha + ')';
    }
    ctx.fillRect(0, 0, w, h);

    // Particle dots
    const dotColor = isDark ? '210, 215, 225' : '255, 255, 255';
    ctx.beginPath();
    for (const p of this.particles) {
      const a = p.alpha * this.overlay;
      if (a < 0.01) continue;
      ctx.moveTo(p.x + p.radius, p.y);
      ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
    }
    ctx.fillStyle = 'rgba(' + dotColor + ', ' + String(this.overlay * 0.8) + ')';
    ctx.fill();
  }
}
